import { FC, Fragment, useState } from 'react';
import {
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	Row,
	Table,
} from 'antd';
import {
	LoadingOutlined,
	UploadOutlined,
	FileSearchOutlined,
	EditOutlined,
} from '@ant-design/icons';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	CustomerReferralDocumentsForm,
	useFRMCustomerPage,
} from './CustomerPage';
import { FileData } from '@/libs/fleet-api/services/types';
import { useModals } from '@/components/modals/ModalProvider';
import { FleetFile } from '@/libs/fleet-api/fetcher';
import dayjs from 'dayjs';
import { useForm } from '@/libs/antd/form';
import { useMutation } from '@tanstack/react-query';
import { uploader } from '@/libs/fleet-api/uploader';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import Upload, { UploadFile } from 'antd/es/upload';
import rule from '@/libs/antd/rules';
import Alert from 'antd/es/alert/Alert';

const FRMCustomerReferral: FC<{ onSubmit: () => void }> = ({ onSubmit }) => {
	const { openPreviewModal } = useModals();
	const { customerForm, referrallForm, isSubmitting, validationStatus } =
		useFRMCustomerPage();

	return (
		<Fragment>
			<Form.Item shouldUpdate>
				{({ getFieldValue }) => {
					const dataSource =
						(getFieldValue(
							customerForm.fieldName('customerReferralDocuments'),
						) as CustomerReferralDocumentsForm[]) ?? [];

					return (
						<Table
							pagination={false}
							bordered
							dataSource={dataSource.map((data, key) => {
								return { ...data, key };
							})}
							columns={[
								{
									title: 'Action',
									width: 1,
									align: 'center',
									render: (_, record, index) => (
										<div
											style={{
												padding: '0',
												display: 'flex',
												alignItems: 'center',
												gap: '20px',
											}}
										>
											<a
												onClick={(e) => {
													e.preventDefault();
													if (record.file?.id) {
														openPreviewModal(record.file as FleetFile);
													}
												}}
												style={
													record.file?.url
														? { display: 'block' }
														: {
																pointerEvents: 'none',
																color: '#bababa',
																display: 'block',
															}
												}
											>
												<FileSearchOutlined />
											</a>
											<a
												style={{ display: 'block' }}
												onClick={(e) => {
													e.preventDefault();
													referrallForm.setModalState(index);
													referrallForm.form.setFieldsValue(record);
												}}
											>
												<EditOutlined />
											</a>
										</div>
									),
								},
								{
									title: 'Jenis Dokumen',
									render: (_, record) => {
										return (
											<>
												{record.documentType}
												{record.isMandatory && (
													<span style={{ color: 'red' }}>*</span>
												)}
											</>
										);
									},
								},
								{
									title: 'No Dokumen',
									dataIndex: ['documentNo'],
								},
								{
									title: 'Tanggal Dokumen',
									render: (_, record) => {
										if (!record.documentDate) return null;
										return dayjs(record.documentDate).format('DD/MM/YYYY');
									},
								},
							]}
						/>
					);
				}}
			</Form.Item>

			<Button
				type="primary"
				style={{
					width: 200,
					display: 'block',
					marginLeft: 'auto',
					marginTop: 60,
				}}
				loading={isSubmitting}
				disabled={!validationStatus.referral}
				onClick={() => onSubmit()}
			>
				SAVE
			</Button>
		</Fragment>
	);
};

export default FRMCustomerReferral;

export const useFRMCustomerReferralForm = () => {
	const [modalState, setModalState] = useState<number>();
	const { form, value, fieldName } = useForm<CustomerReferralDocumentsForm>();

	return {
		form,
		value,
		fieldName,
		modalState,
		setModalState,
	};
};

export const FRMCustomerReferralForm: FC<
	ReturnType<typeof useFRMCustomerReferralForm>
> = (props) => {
	const { customerForm, customerId } = useFRMCustomerPage();
	const { form, value, fieldName, modalState, setModalState } = props;
	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	return (
		<Form
			{...ANTD_DEFAULT_PROPS.FORM}
			form={form}
			onFinish={(values) => {
				setModalState(undefined);
				customerForm.form.setFieldValue(
					customerForm.fieldName('customerReferralDocuments'),
					customerForm.value?.customerReferralDocuments?.map(
						(doc, docIndex) => {
							if (modalState === docIndex) return values;
							return doc;
						},
					),
				);
			}}
		>
			<FleetModal
				open={Boolean(typeof modalState === 'number')}
				width={940}
				closeIcon={false}
				keyboard={false}
				maskClosable={false}
				buttonPosition="end"
				okButtonProps={{
					disabled: uploadMutation.isPending,
				}}
				onOk={() => form?.submit()}
				onCancel={() => setModalState(undefined)}
				afterClose={() => form.resetFields()}
			>
				<FleetTitle>Edit Referral</FleetTitle>
				<div style={{ marginBottom: 20 }}></div>
				<Form.Item
					noStyle
					name={fieldName('id')}
				/>
				<Form.Item
					noStyle
					name={fieldName('isMandatory')}
				/>
				<Form.Item
					noStyle
					name={fieldName('defaultDocumentNo')}
				/>
				<Form.Item
					noStyle
					name={fieldName('isDocumentDateMandatory')}
				/>
				<Form.Item
					noStyle
					name={fieldName('isDocumentNoMandatory')}
				/>
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('documentType')}
							label="Jenis Dokumen"
							labelCol={{ span: 8 }}
						>
							<Input.TextArea
								rows={5}
								disabled
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						{value?.id === 38 || value?.id === 41 ? (
							<Form.Item
								name={fieldName('documentNo')}
								label="No. Dokumen"
								labelCol={{ span: 8 }}
								initialValue="-"
							>
								<Input disabled />
							</Form.Item>
						) : (
							<Form.Item
								name={fieldName('documentNo')}
								label="No. Dokumen"
								labelCol={{ span: 8 }}
								rules={value?.isDocumentNoMandatory ? [rule.required()] : []}
							>
								<Input disabled={value?.documentType === 'NPWP Perusahaan'} />
							</Form.Item>
						)}
					</Col>
				</Row>
				<Divider />
				{value?.id === 38 ? (
					<Fragment>
						<Row gutter={[40, 20]}>
							<Col span={12}>
								<Form.Item
									name={fieldName('documentDate')}
									label="Tanggal Dokumen"
									labelCol={{ span: 8 }}
									getValueProps={(value) => ({
										value: value && dayjs(value),
									})}
									normalize={(value) =>
										value && dayjs(value).utc(true).toISOString()
									}
									rules={
										value?.isDocumentDateMandatory ? [rule.required()] : []
									}
								>
									<DatePicker
										disabledDate={(now) => now > dayjs().subtract(1, 'day')}
									/>
								</Form.Item>
							</Col>
						</Row>
						<Alert
							message="Tanggal dokumen yang diinputkan merupakan tanggal diterimanya dokumen CES"
							type="warning"
							showIcon
							className="custom-alert-info"
							style={{ marginTop: 20 }}
						/>
						<Divider />
						<Row gutter={[40, 20]}>
							<Col span={12}>
								<Form.Item
									name={fieldName('file')}
									required
									label="Upload"
									labelCol={{ span: 8 }}
									getValueProps={(value) => {
										const file = value as FileData;
										return {
											fileList: file?.id
												? ([
														{
															uid: file.id,
															name: file.name,
															thumbUrl: file.url,
														},
													] as UploadFile[])
												: [],
										};
									}}
									normalize={({ file }: { file: UploadFile }) => {
										return {
											id: file.uid,
											format: file.type,
											name: file.name,
											url: file.thumbUrl,
										} as FileData;
									}}
									rules={[rule.required()]}
								>
									<Upload.Dragger
										listType="picture"
										accept=".pdf,.jpg,.jpeg,.png"
										maxCount={1}
										beforeUpload={() => false}
										onRemove={() => {
											form.setFieldValue(fieldName('file'), null);
											return false;
										}}
										onChange={async ({ file }) => {
											const fileid = Date.now().toString();
											const filepath = customerId
												? `customer/${customerId}/referral-document`
												: 'customer/temp/referral-document';

											try {
												const url = await uploadMutation.mutateAsync({
													file: file as unknown as File,
													fileid,
													filepath,
												});

												form.setFieldValue(fieldName('file'), {
													format: 'jpeg',
													id: fileid,
													name: file.name,
													path: filepath,
													url,
												});
											} catch {
												form.setFieldValue(fieldName('file'), null);
											}
										}}
									>
										{uploadMutation.isPending ? (
											<LoadingOutlined
												style={{
													fontSize: 25,
													color: '#BABABA',
													display: 'block',
												}}
											/>
										) : (
											<UploadOutlined
												style={{
													fontSize: 25,
													color: '#BABABA',
													display: 'block',
												}}
											/>
										)}
									</Upload.Dragger>
								</Form.Item>
							</Col>
						</Row>
					</Fragment>
				) : (
					<Row gutter={[40, 20]}>
						<Col span={12}>
							<Form.Item
								name={fieldName('documentDate')}
								label="Tanggal Dokumen"
								labelCol={{ span: 8 }}
								getValueProps={(value) => ({
									value: value && dayjs(value),
								})}
								normalize={(value) =>
									value && dayjs(value).utc(true).toISOString()
								}
								rules={value?.isDocumentDateMandatory ? [rule.required()] : []}
							>
								<DatePicker
									disabledDate={(now) => now > dayjs().subtract(1, 'day')}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name={fieldName('file')}
								required
								label="Upload"
								labelCol={{ span: 8 }}
								getValueProps={(value) => {
									const file = value as FileData;
									return {
										fileList: file?.id
											? ([
													{
														uid: file.id,
														name: file.name,
														thumbUrl: file.url,
													},
												] as UploadFile[])
											: [],
									};
								}}
								normalize={({ file }: { file: UploadFile }) => {
									return {
										id: file.uid,
										format: file.type,
										name: file.name,
										url: file.thumbUrl,
									} as FileData;
								}}
								rules={[rule.required()]}
							>
								<Upload.Dragger
									listType="picture"
									accept=".pdf,.jpg,.jpeg,.png"
									maxCount={1}
									beforeUpload={() => false}
									onRemove={() => {
										form.setFieldValue(fieldName('file'), null);
										return false;
									}}
									onChange={async ({ file }) => {
										const fileid = Date.now().toString();
										const filepath = customerId
											? `customer/${customerId}/referral-document`
											: 'customer/temp/referral-document';

										try {
											const url = await uploadMutation.mutateAsync({
												file: file as unknown as File,
												fileid,
												filepath,
											});

											form.setFieldValue(fieldName('file'), {
												format: 'jpeg',
												id: fileid,
												name: file.name,
												path: filepath,
												url,
											});
										} catch {
											form.setFieldValue(fieldName('file'), null);
										}
									}}
								>
									{uploadMutation.isPending ? (
										<LoadingOutlined
											style={{
												fontSize: 25,
												color: '#BABABA',
												display: 'block',
											}}
										/>
									) : (
										<UploadOutlined
											style={{
												fontSize: 25,
												color: '#BABABA',
												display: 'block',
											}}
										/>
									)}
								</Upload.Dragger>
							</Form.Item>
						</Col>
					</Row>
				)}
			</FleetModal>
		</Form>
	);
};
